import { InjectionToken } from '@angular/core';
import type { AiResultAction, AiRunnerConfig } from './ai-runner.model';

/**
 * Quem pode o quê no runner. Todas as checagens são opcionais e síncronas; ausente = permitido.
 * O backend continua sendo a autoridade (devolve 403) — isto só evita oferecer o que falharia.
 */
export interface AiRunnerAccessPolicy {
  /** Pode abrir/ver o runner. */
  canOpen?(config: AiRunnerConfig): boolean;
  /** Pode iniciar uma execução. */
  canRun?(config: AiRunnerConfig): boolean;
  /** Pode aplicar o resultado: sem `action`, as atualizações de tela; com `action`, aquela ação. */
  canApply?(config: AiRunnerConfig, action?: AiResultAction): boolean;
  /** Mensagem mostrada quando `canRun` nega. */
  denyReason?(config: AiRunnerConfig): string | null;
}

/** O que fazer quando `canOpen`/`canRun` negam. */
export type AiRunnerDeniedMode = 'readonly' | 'block';

export interface AiRunnerConfigAccess extends AiRunnerAccessPolicy {
  /** `readonly` (padrão): abre sem permitir executar; `block`: não abre. */
  onDenied?: AiRunnerDeniedMode;
}

/** Política global (por `providers`); `config.access` sobrescreve campo a campo. */
export const AI_RUNNER_ACCESS = new InjectionToken<AiRunnerAccessPolicy>('AI_RUNNER_ACCESS', {
  providedIn: 'root',
  factory: () => ({}),
});

export interface ResolvedAiRunnerAccess {
  canOpen: boolean;
  canRun: boolean;
  denyReason: string | null;
  onDenied: AiRunnerDeniedMode;
  canApply(action?: AiResultAction): boolean;
}

/**
 * Resolve a política efetiva: `config.access` por cima do token global. Também serve ao host para
 * esconder o botão que abre o runner (`resolveAiRunnerAccess(policy, config).canOpen`).
 */
export function resolveAiRunnerAccess(
  global: AiRunnerAccessPolicy,
  config: AiRunnerConfig
): ResolvedAiRunnerAccess {
  const local: AiRunnerConfigAccess = config.access ?? {};
  const pick = <K extends keyof AiRunnerAccessPolicy>(key: K) => local[key] ?? global[key];
  const canOpen = pick('canOpen')?.call(local, config) ?? true;
  const canRun = canOpen && (pick('canRun')?.call(local, config) ?? true);
  const canApply = pick('canApply');
  return {
    canOpen,
    canRun,
    denyReason: canRun ? null : (pick('denyReason')?.call(local, config) ?? null),
    onDenied: local.onDenied ?? 'readonly',
    canApply: action => canOpen && (canApply?.call(local, config, action) ?? true),
  };
}
